import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchCompanyFootprint } from '@/api/(admin)/companies';
import { useTranslator } from '@/hooks/useTranslator';
import { footprintCategories, footprintRows } from '../-utils/techInsights';

/** Shared by the tab's route loader and the hook. */
export function companyFootprintQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: ['admin', 'companies', 'footprint', companyId] as const,
    queryFn: () => fetchCompanyFootprint(companyId),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

/**
 * What this company has, table by table and module by module, plus the
 * per-module totals the chart draws. Module names are the merge page's, so the
 * two screens name a module the same way.
 */
export function useCompanyTechInsights(companyId: string) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const query = useQuery(companyFootprintQueryOptions(companyId));
  const footprint = query.data?.data;

  return {
    rows: useMemo(
      () =>
        footprint
          ? footprintRows(
              footprint,
              key => t(`merge.groups.${key}`),
              t('detail.techInsights.activity'),
            )
          : [],
      [footprint, t],
    ),
    categories: useMemo(
      () =>
        footprint
          ? footprintCategories(footprint, key => t(`merge.groups.${key}`))
          : [],
      [footprint, t],
    ),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
